import React from 'react';
import { Edit3, Eye, Settings, ShieldAlert, Check, LogOut } from 'lucide-react';
import { motion } from 'motion/react';

interface VisualEditorBarProps {
  isAdminAuthenticated: boolean;
  isVisualEditActive: boolean;
  onToggleVisualEdit: () => void;
  onOpenAdmin: () => void;
  onLogout: () => void;
}

export function VisualEditorBar({
  isAdminAuthenticated,
  isVisualEditActive,
  onToggleVisualEdit,
  onOpenAdmin,
  onLogout
}: VisualEditorBarProps) {
  if (!isAdminAuthenticated) return null;

  return (
    <motion.div
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -60, opacity: 0 }}
      className="fixed top-20 left-0 right-0 z-40 bg-zinc-950/95 border-b border-amber-400/40 shadow-2xl backdrop-blur-md px-4 py-2.5 text-white font-sans text-xs"
    >
      <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-amber-400/10 border border-amber-400/30 px-2.5 py-1 text-amber-400 font-mono text-[11px] font-bold uppercase tracking-wider">
            <Edit3 className="w-3.5 h-3.5 animate-pulse" />
            <span>Mode Admin Session</span>
          </div>

          <p className="hidden md:block text-zinc-300 text-xs">
            {isVisualEditActive ? (
              <span className="text-amber-300 font-medium">
                ✨ <strong className="text-white font-bold">Édition directe activée :</strong> Cliquez sur n'importe quel titre, paragraphe ou chiffre (icône ✏️) pour le modifier immédiatement !
              </span>
            ) : (
              <span className="text-zinc-400">
                Activez l'édition directe pour modifier les textes du site directement en un clic.
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2.5 ml-auto">
          {/* Toggle Visual Edit Button */}
          <button
            onClick={onToggleVisualEdit}
            className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider font-bold border transition-all flex items-center gap-1.5 cursor-pointer ${
              isVisualEditActive
                ? 'bg-amber-400 text-black border-amber-400 shadow-lg shadow-amber-400/20'
                : 'bg-zinc-900 text-amber-400 border-amber-400/40 hover:border-amber-400'
            }`}
          >
            {isVisualEditActive ? (
              <>
                <Check className="w-3.5 h-3.5" />
                <span>Crayon Direct : ACTIF</span>
              </>
            ) : (
              <>
                <Edit3 className="w-3.5 h-3.5" />
                <span>Activer Crayon (✏️)</span>
              </>
            )}
          </button>

          {/* Open Full Admin Panel Button */}
          <button
            onClick={onOpenAdmin}
            className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-600 font-mono text-[11px] uppercase tracking-wider transition-colors flex items-center gap-1.5 cursor-pointer"
            title="Ouvrir la gestion des vidéos, photos, SEO et galeries"
          >
            <Settings className="w-3.5 h-3.5 text-amber-400" />
            <span>Panneau Complet</span>
          </button>

          {/* Logout button */}
          <button
            onClick={onLogout}
            className="px-3 py-1.5 bg-red-950/60 hover:bg-red-900/80 text-red-200 border border-red-500/40 font-mono text-[11px] uppercase tracking-wider transition-colors flex items-center gap-1.5 cursor-pointer"
            title="Se déconnecter de la session administrateur"
          >
            <LogOut className="w-3.5 h-3.5 text-red-400" />
            <span>Déconnexion</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
}
